// ==================== PastAttendance.jsx ====================
import React, { useEffect, useState } from "react";
import { UserCheck } from "lucide-react";
import "../Styling/PastAttendance.css";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import PastAttendanceModal from "./PastAttendanceModal";

const PastAttendance = () => {
  const { user } = useAuth();
  const userId = user?.userId;
  const orgId = user?.organisationId;

  const [attendanceData, setAttendanceData] = useState([]);
  const [holidays, setHolidays] = useState({});
  const [leaves, setLeaves] = useState({});
  const [showModal, setShowModal] = useState(false);

  /* ================= Fetch Past 15 Days Attendance ================= */
  useEffect(() => {
    if (!userId) return;

    const fetchAttendance = async () => {
      try {
        const res = await fetch(
          `${Base_URL}/Attendance/GetPast15DaysAttendance/${userId}`
        );
        const data = await res.json();

        setAttendanceData(
          Array.isArray(data)
            ? data.map(d => ({
                date: d.date,
                status: d.status,
                hours: d.hours
              }))
            : []
        );
      } catch (err) {
        console.error("❌ Attendance error:", err);
      }
    };

    fetchAttendance();
  }, [userId]);

  /* ================= Fetch Holidays ================= */
  useEffect(() => {
    if (!orgId) return;

    const fetchHolidays = async () => {
      try {
        const res = await fetch(
          `${Base_URL}/HolidayMasterOrg/GetHolidaysByOrg/${orgId}`
        );
        const data = await res.json();

        const map = {};
        data
          .filter(
            h =>
              h.holidayType === "National" ||
              h.holidayType === "Festival"
          )
          .forEach(h => {
            map[new Date(h.holidayDate).toDateString()] = h.holidayName;
          });

        setHolidays(map);
      } catch (err) {
        console.error("❌ Holiday error:", err);
      }
    };

    fetchHolidays();
  }, [orgId]);

  /* ================= Fetch Past Leaves ================= */
  useEffect(() => {
    if (!userId) return;

    const fetchLeaves = async () => {
      try {
        const res = await fetch(
          `${Base_URL}/Leave/past-leaves/${userId}`
        );
        const data = await res.json();

        const map = {};
        data.data.forEach(l => {
          const from = new Date(l.fromDate);
          const to = new Date(l.toDate);

          for (
            let d = new Date(from);
            d <= to;
            d.setDate(d.getDate() + 1)
          ) {
            map[d.toDateString()] = l.leaveType;
          }
        });

        setLeaves(map);
      } catch (err) {
        console.error("❌ Leave error:", err);
      }
    };

    fetchLeaves();
  }, [userId]);

  /* ================= Helpers ================= */
  const isWeekend = date => {
    const day = new Date(date).getDay();

    // Org 2001 → Only Sunday off
    if (orgId === 2001) {
      return day === 0; // Sunday
    }

    // Other orgs → Saturday + Sunday off
    return day === 0 || day === 6;
  };

  /* ================= CALCULATIONS ================= */
  const workingDays = attendanceData.filter(day => {
    const key = new Date(day.date).toDateString();
    return (
      !isWeekend(day.date) &&
      !holidays[key] &&
      !leaves[key]
    );
  });

  const presentCount = workingDays.filter(
    d =>
      d.status?.toLowerCase() === "present" ||
      d.status?.toLowerCase() === "wfh"
  ).length;

  const absentCount = workingDays.length - presentCount;

  const leaveCount = attendanceData.filter(day => {
    const key = new Date(day.date).toDateString();
    return leaves[key];
  }).length;

  /* ================= UI ================= */
  return (
    <div className="pastattendance-card compact">
      {/* Header */}
      <div className="pastattendance-header compact">
        <div className="pastattendance-icon small">
          <UserCheck size={16} />
        </div>
        <h4>Past Attendance</h4>

        <span
          className="header-badge clickable"
          onClick={() => setShowModal(true)}
        >
          View This Month
        </span>
      </div>

      {/* Attendance Strip */}
      <div className="attendance-strip">
        {attendanceData.map((day, idx) => {
          const key = new Date(day.date).toDateString();
          const isHoliday = holidays[key];
          const isLeave = leaves[key];
          const isWeekOff = isWeekend(day.date);

          let pillClass = "";
          let pillText = "";
          let title = "";

          if (isHoliday) {
            pillClass = "holiday";
            pillText = "Holiday";
            title = `Holiday • ${holidays[key]}`;
          } else if (isLeave) {
            pillClass = "leave";
            pillText = "Leave";
            title = `Leave • ${leaves[key]}`;
          } else if (isWeekOff) {
            pillClass = "weekoff";
            pillText = "Off";
            title = "Weekend";
          } else {
            pillClass = (day.status || "").toLowerCase();
            pillText = day.hours;
            title = `${day.date} • ${day.status} • ${day.hours}`;
          }

          return (
            <div
              key={idx}
              className={`attendance-pill ${pillClass}`}
              title={title}
            >
              <span className="pill-date">
                {new Date(day.date).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short"
                })}
              </span>
              <span className="pill-hours">{pillText}</span>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      <PastAttendanceModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        userId={userId}
      />

      {/* Legend */}
      <div className="attendance-legend">
        <span><i className="dot present"></i> Present: {presentCount}</span>
        <span><i className="dot absent"></i> Absent: {absentCount}</span>
        <span><i className="dot leave"></i> Leave: {leaveCount}</span>
        <span><i className="dot holiday"></i> Holiday</span>
        <span><i className="dot weekoff"></i> Week Off</span>
      </div>
    </div>
  );
};

export default PastAttendance;
